import React from 'react';

import Textarea from '@packages/systems/design-system/Textarea';
import Hitarea from '@packages/systems/design-system/Hitarea';
import {DeprecatedPopoverBridge} from '@packages/systems/design-system/PopoverBridge';
import {styled, styledDiv, emotionStyled} from '@packages/utilities/styled';
import {Subheading} from '@packages/systems/design-system/Text';
import SVGButton from '@packages/systems/design-system/SVGButton';
import {isNil} from 'lodash';
import {
  TransformRotate as RotateIcon,
  CrossLarge,
} from '@packages/systems/ui/icons';

type Props = {
  error: any;
  onCommit: (value: string, error?: any) => void;
  children?: any;
  dataAutomationIdPrefix?: string;
  mini?: boolean;
  showMask: boolean | 'TRANSPARENT';
};

type State = {
  errorMsg: string;
  savedMsg: string | null;
};

const Content = styledDiv(({theme, mini}) => {
  const miniWidth =
    theme.gutters.x20 * 10 + theme.gutters.wide + theme.gutters.x4; // 214px
  const width = theme.gutters.x20 * 11 + theme.gutters.x4; // 224px
  return {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'stretch',
    width: mini ? miniWidth : width,
    padding: theme.gutters.x8,
  };
});

const StyledTextArea = styled(Textarea)(({theme}) => ({
  height: theme.gutters.x20 * 5 + theme.gutters.x12,
  resize: 'none',
  padding: theme.gutters.x4,
  fontSize: '12px',
  marginBottom: theme.gutters.x8,
}));

const RestoreDefault = styledDiv(
  ({theme, hidden}) => ({
    marginTop: theme.gutters.x8,
    color: theme.colors.foregroundDim,
    cursor: 'default',
    display: hidden ? 'none' : 'flex',
    position: 'relative',
    float: 'left',
    outline: 'none',
  }),
  {
    hover: ({theme}) => ({
      color: theme.colors.foreground,
    }),
  }
);

const StyledRotateIcon = styled(RotateIcon)(() => ({
  margin: '3px 5px 0 0',
  height: '10px',
  width: '10px',
}));

const Save = styledDiv(
  ({theme, disabled}) => ({
    padding: `0 ${theme.gutters.wide}px`,
    marginTop: theme.gutters.x4,
    color: theme.colors.foreground,
    backgroundColor: theme.colors.backgroundVivid,
    fontSize: '11px',
    lineHeight: '22px',
    height: '22px',
    width: 'auto',
    display: 'block',
    float: 'right',
    position: 'relative',
    boxSizing: 'content-box',
    textAlign: 'center',
    textDecoration: 'none',
    outline: 'none',
    userSelect: 'none',
    border: `${theme.control.borderWidth}px solid ${theme.colors.border}`,
    borderRadius: theme.control.borderRadius,
    cursor: 'default',
    opacity: disabled ? theme.opacity.disabled : 1,
    pointerEvents: disabled ? 'none' : 'all',
  }),
  {
    hover: ({theme}) => ({
      backgroundColor: theme.colors.backgroundVividest,
      color: theme.colors.foregroundVivid,
    }),
  }
);

const Title = emotionStyled('header')({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'flex-start',
});
const HeaderContainer = styledDiv(({theme}) => ({
  display: 'flex',
  flexDirection: 'row',
  alignItems: 'center',
  justifyContent: 'space-between',
  paddingBottom: theme.gutters.x8,
  marginBottom: theme.gutters.x8,
  borderBottom: `${theme.control.borderWidth}px solid ${theme.control.borderColor}`,
}));
const FooterContainer = emotionStyled('div')({
  display: 'flex',
  flexDirection: 'row-reverse',
  alignItems: 'center',
  justifyContent: 'space-between',
});

const CrossIcon = styled(CrossLarge)(({theme}) => ({
  width: theme.gutters.x12,
  height: theme.gutters.x12,
  maxHeight: theme.gutters.x12,
}));

const TooltipContent = ({
  // @ts-expect-error - TS7031 - Binding element 'children' implicitly has an 'any' type.
  children,
  // @ts-expect-error - TS7031 - Binding element 'onClose' implicitly has an 'any' type.
  onClose,
  showHeader = true,
  title = '',
  // @ts-expect-error - TS7031 - Binding element 'mini' implicitly has an 'any' type.
  mini,
}) => {
  return (
    <Content mini={mini}>
      {showHeader && (
        <HeaderContainer>
          <Title>
            {/* @ts-expect-error - TS2769 - No overload matches this call. */}
            <Subheading small>{title}</Subheading>
          </Title>
          <SVGButton onClick={onClose} icon={<CrossIcon />} />
        </HeaderContainer>
      )}
      {children}
    </Content>
  );
};

export default class MultiErrorMessagePanel extends React.Component<
  Props,
  State
> {
  state = {
    errorMsg: isNil(this.props.error.message)
      ? this.props.error.messageDefault
      : this.props.error.message,
    savedMsg: null,
  };

  onErrorMsgChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target && e.target.value !== undefined) {
      this.setState({errorMsg: e.target.value});
    }
  };

  onRestore = () => {
    this.setState({errorMsg: this.props.error.messageDefault});
  };

  onDismiss = () => {
    const {savedMsg} = this.state;
    const {message, messageDefault} = this.props.error;
    const newErrorMsg = isNil(savedMsg)
      ? isNil(message)
        ? messageDefault
        : message
      : savedMsg;
    if (newErrorMsg) {
      this.setState({errorMsg: newErrorMsg});
    }
  };

  render() {
    const {children, dataAutomationIdPrefix, mini, onCommit, showMask} =
      this.props;
    const errorMsgDefault = this.props.error.messageDefault;
    const errorMsg = this.props.error.message;

    const isDisabled = this.state.errorMsg === errorMsg ? 'disabled' : '';
    const isHidden = this.state.errorMsg === errorMsgDefault ? 'hidden' : '';

    const modalAutomationId = dataAutomationIdPrefix
      ? dataAutomationIdPrefix + '-edit-modal'
      : '';
    const textAutomationId = dataAutomationIdPrefix
      ? dataAutomationIdPrefix + '-edit-modal-text'
      : '';
    const saveAutomationId = dataAutomationIdPrefix
      ? dataAutomationIdPrefix + '-edit-modal-save'
      : '';
    const restoreAutomationId = dataAutomationIdPrefix
      ? dataAutomationIdPrefix + '-edit-modal-restore'
      : '';

    // @ts-expect-error - TS7031 - Binding element 'closePopover' implicitly has an 'any' type.
    const renderPopoverContent = ({closePopover}) => (
      <TooltipContent
        onClose={() => {
          closePopover();
          this.onDismiss();
        }}
        title="Edit Error Message"
        mini={mini}
      >
        <StyledTextArea
          data-prevent-global-event-handlers="true"
          data-automation-id={textAutomationId}
          text={this.state.errorMsg}
          value={this.state.errorMsg}
          onChange={this.onErrorMsgChange}
        />
        <FooterContainer>
          <Hitarea>
            <Save
              disabled={isDisabled}
              // eslint-disable-next-line jsx-a11y/tabindex-no-positive
              tabIndex={2}
              onClick={() => {
                this.setState({savedMsg: this.state.errorMsg}, () => {
                  closePopover();
                  onCommit(this.state.errorMsg);
                });
              }}
              data-automation-id={saveAutomationId}
            >
              Save
            </Save>
          </Hitarea>
          <Hitarea>
            <RestoreDefault
              hidden={isHidden}
              tabIndex={0}
              onClick={this.onRestore}
              data-automation-id={restoreAutomationId}
            >
              <StyledRotateIcon />
              Restore default
            </RestoreDefault>
          </Hitarea>
        </FooterContainer>
      </TooltipContent>
    );

    return children ? (
      <DeprecatedPopoverBridge
        renderPopoverContent={renderPopoverContent}
        showArrow={false}
        data-automation-id={modalAutomationId}
        showMask={showMask}
        onChange={(popoverState) => {
          if (!popoverState.open) this.onDismiss();
        }}
        metricsIdentifier="multi-error-message-panel-popover"
      >
        {children}
      </DeprecatedPopoverBridge>
    ) : null;
  }
}
